// WeeklyMenuGenerator.jsx
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Card, Form, Button, Spinner, Alert, Row, Col, ToggleButtonGroup, ToggleButton, Table, Modal as BootstrapModal, InputGroup, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCheckCircle, faCalendarAlt, faHistory,
    faSeedling, faUsers, faInfoCircle,
    faBullseye, faUtensils, faListAlt, faTableCellsLarge, faFire
} from '@fortawesome/free-solid-svg-icons';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';

import { callPollinationsTextApi } from '../services/aiService';
import { getMenuHistory, hideUserActivity } from '../services/dbService';

import ViewModeLayout from './ui/ViewModeLayout';
import HistoryView from './ui/HistoryView';
import ItemDetailsModal from './ui/ItemDetailsModal';
import MacronutrientChart from './ui/MacronutrientChart';
import PreferenceForm from './ui/PreferenceForm';
import RecipeDisplay from './ui/RecipeDisplay';


// --- Función Auxiliar para Generar Detalles de UNA Receta ---
const generateSingleRecipeDetailsAI = async ({
    mealIdea,
    targetCalories,
    targetMacros, // { protein, carbs, fat } en gramos
    servings,
    dietPreference, // e.g., 'vegan', 'keto', 'other'
    otherDietOrCuisine, // e.g., 'My specific diet details' if dietPreference is 'other'
    customPromptText,
    effectiveOverallRestrictions, // Array de strings ya procesado
    userHealthGoals,
}) => {
    let baseQuery = `a recipe for "${mealIdea}".`;

    let resolvedDietOrCuisine = dietPreference;
    if (dietPreference === 'other' && otherDietOrCuisine && otherDietOrCuisine.trim() !== '') {
        resolvedDietOrCuisine = otherDietOrCuisine.trim();
    } else if (dietPreference === 'other') { // 'other' selected but not specified
        resolvedDietOrCuisine = ''; // Default to generally healthy
    }


    if (resolvedDietOrCuisine && resolvedDietOrCuisine.trim() !== '') {
        baseQuery += ` Align with ${resolvedDietOrCuisine} style.`;
    } else {
        baseQuery += ` Generally healthy.`;
    }

    baseQuery += ` For ${servings} serving(s).`;
    if (targetCalories) {
        baseQuery += ` Target calories per serving: approximately ${targetCalories} kcal.`;
    }
    if (targetMacros) {
        baseQuery += ` Target macronutrient breakdown per serving: Protein ~${targetMacros.protein}g, Carbs ~${targetMacros.carbs}g, Fat ~${targetMacros.fat}g.`;
    }
    if (customPromptText && customPromptText.trim()) baseQuery += ` Additional Instructions for this meal: ${customPromptText.trim()}.`;

    let goalContext = '';
    if (Array.isArray(userHealthGoals) && userHealthGoals.length > 0) {
        goalContext = 'User Health Goals (context for recipe choices):\n' + userHealthGoals.map(g => `- ${g?.target || 'Goal'}${g?.deadline ? ` (by ${g.deadline})` : ''}`).join('\n') + '\n\n';
    }

    let restrictionContext = '';
    if (effectiveOverallRestrictions && effectiveOverallRestrictions.length > 0) {
        restrictionContext = 'Strictly AVOID these ingredients/foods: ' + effectiveOverallRestrictions.join(", ") + '.\n\n';
    }

    const systemPrompt = `You are a highly skilled cooking assistant and creative nutritionist.
User Context for this specific recipe:
${goalContext}${restrictionContext}User Request: ${baseQuery}

Output Format: Return ONLY a valid JSON object with the following structure:
{ 
  "recipeName": "string (Creative name for: ${mealIdea})", 
  "description": "string (1-2 enticing sentences)",
  "prepTime": "string (e.g., '15 minutes')",
  "cookTime": "string (e.g., '25 minutes')", 
  "servings": ${servings},
  "estimatedCaloriesPerServing": "number (MUST be close to ${targetCalories || 'target if provided, else your best estimate based on ingredients'})",
  "macrosPerServing": { 
    "protein": "number (grams, MUST be close to target if provided)",
    "carbs": "number (grams, MUST be close to target if provided)",
    "fat": "number (grams, MUST be close to target if provided)"
  },
  "macrosPer100g": { "protein": "number", "carbs": "number", "fat": "number (General estimate for the dish per 100g)" },
  "cuisineType": "string (e.g., Italian, Mexican - infer if not specified)",
  "ingredients": ["string (e.g., '1 large chicken breast (approx. 200g), cubed', amounts for ${servings} servings)"], 
  "steps": ["string (Step-by-step, concise and clear)"], 
  "notes": ["string (Optional: Chef's Tips, variations, substitutions)"] 
}

CRITICAL REQUIREMENTS:
- "estimatedCaloriesPerServing" MUST be a number. If a target calorie was provided in the request, the recipe's calories MUST closely match it.
- "macrosPerServing" (protein, carbs, fat in grams) MUST be numbers. If target macros were provided, the recipe's macros MUST closely match them.
- The sum of macrosPerServing (Protein*4 + Carbs*4 + Fat*9 kcal) MUST closely approximate "estimatedCaloriesPerServing".
- Adapt all ingredient amounts to be appropriate for the specified number of servings (${servings}).
- Adhere strictly to all dietary preferences and restrictions mentioned.
Return ONLY the valid JSON object. No extra text.`;

    try {
        const recipeDetails = await callPollinationsTextApi(systemPrompt, `Generate detailed recipe for ${mealIdea}.`);
        if (recipeDetails && typeof recipeDetails.recipeName === 'string' &&
            typeof recipeDetails.estimatedCaloriesPerServing === 'number' &&
            recipeDetails.macrosPerServing && typeof recipeDetails.macrosPerServing.protein === 'number') {
            return { ...recipeDetails, servings };
        }
        console.warn(`Invalid recipe details structure from AI for "${mealIdea}":`, recipeDetails);
        return null;
    } catch (error) {
        console.error(`Error generating details for recipe "${mealIdea}":`, error);
        throw error;
    }
};


// Standalone Menu Table Component
const StandaloneMenuTable = ({ menuData, onOpenMealDetail, isHistoryContext = false }) => {
    if (!menuData || Object.keys(menuData).length === 0) {
        return <Alert variant="light" className="text-center">No menu data to display.</Alert>;
    }

    const getMealKeysForTable = (displayMenu) => {
        for (const dayKey in displayMenu) {
            if (dayKey.startsWith("day") && displayMenu[dayKey] && displayMenu[dayKey].meals) {
                return Object.keys(displayMenu[dayKey].meals);
            }
        }
        return [];
    };
    const mealTableKeys = getMealKeysForTable(menuData);

    const hasAnyDailyMacroData = Object.values(menuData).some(
        day => day && typeof day === 'object' && day.macros &&
            typeof day.macros.protein === 'number' &&
            typeof day.macros.carbs === 'number' &&
            typeof day.macros.fat === 'number'
    );

    return (
        <Card className="shadow-sm mb-4">
            <Card.Header className={`${isHistoryContext ? 'bg-info' : 'bg-primary'} text-white d-flex justify-content-between align-items-center`}>
                <span><FontAwesomeIcon icon={faTableCellsLarge} className="me-2" />Weekly Menu Table</span>
                {menuData["day 1"]?.dailyCalories &&
                    <Badge bg="light" text="dark" className="ms-2">
                        Avg. {menuData["day 1"].dailyCalories} Cal/day
                    </Badge>
                }
            </Card.Header>
            <Card.Body>
                <Table striped bordered hover responsive size="sm" className="menu-table">
                    <thead>
                        <tr className="text-center align-middle">
                            <th style={{ minWidth: '80px' }}>Day</th>
                            {mealTableKeys.map(mealNameKey => (
                                <th key={mealNameKey} style={{ minWidth: '200px' }}>
                                    {mealNameKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                </th>
                            ))}
                            {hasAnyDailyMacroData && <th style={{ minWidth: isHistoryContext ? '200px' : '150px' }}>Daily Totals</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {Object.entries(menuData)
                            .filter(([key, value]) => key.startsWith("day") && typeof value === 'object' && value !== null && value.meals)
                            .map(([dayLabel, dayData]) => (
                                <tr key={dayLabel}>
                                    <td className='fw-bold align-middle text-center'>{dayLabel.charAt(0).toUpperCase() + dayLabel.slice(1)}</td>
                                    {mealTableKeys.map(mealNameKey => {
                                        const mealObject = dayData.meals[mealNameKey];
                                        return (
                                            <td key={`${dayLabel}-${mealNameKey}`} className="align-middle" style={{ verticalAlign: 'top', fontSize: '0.85rem' }}>
                                                {mealObject && mealObject.recipeName ? (
                                                    <div>
                                                        <strong className="d-block">{mealObject.recipeName}</strong>
                                                        {typeof mealObject.estimatedCaloriesPerServing === 'number' &&
                                                            <span className="text-muted d-block" style={{ fontSize: '0.75rem' }}>
                                                                {mealObject.estimatedCaloriesPerServing} kcal
                                                                <FontAwesomeIcon icon={faFire} className="ms-1 text-danger" />
                                                            </span>
                                                        }
                                                        {mealObject.macrosPerServing && (
                                                            <span className="text-muted d-block" style={{ fontSize: '0.70rem' }}>
                                                                P: {mealObject.macrosPerServing.protein?.toFixed(0)}g,
                                                                C: {mealObject.macrosPerServing.carbs?.toFixed(0)}g,
                                                                F: {mealObject.macrosPerServing.fat?.toFixed(0)}g
                                                            </span>
                                                        )}
                                                        {typeof onOpenMealDetail === 'function' && (
                                                            <Button
                                                                size="sm" variant="link" className="p-0 float-end text-info mt-1"
                                                                style={{ lineHeight: 1 }}
                                                                onClick={() => onOpenMealDetail(dayLabel, mealNameKey, mealObject)}
                                                                title="View Details"
                                                            ><FontAwesomeIcon icon={faInfoCircle} /></Button>
                                                        )}
                                                    </div>
                                                ) : <span className="text-muted fst-italic">{mealObject?.recipeName || "Processing..."}</span>}
                                            </td>
                                        );
                                    })}
                                    {hasAnyDailyMacroData && (
                                        <td className="text-center p-1 align-middle">
                                            {dayData.macros &&
                                                typeof dayData.macros.protein === 'number' &&
                                                typeof dayData.macros.carbs === 'number' &&
                                                typeof dayData.macros.fat === 'number' ? (
                                                <div style={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    minHeight: '120px'
                                                }}>
                                                    <MacronutrientChart
                                                        proteinGrams={parseFloat(dayData.macros.protein) || 0}
                                                        carbsGrams={parseFloat(dayData.macros.carbs) || 0}
                                                        fatGrams={parseFloat(dayData.macros.fat) || 0}
                                                    />
                                                    <span className="text-muted small mt-1 d-block" style={{ fontSize: '0.75rem' }}>
                                                        P: {dayData.macros.protein?.toFixed(0)}g, C: {dayData.macros.carbs?.toFixed(0)}g, F: {dayData.macros.fat?.toFixed(0)}g
                                                        {dayData.dailyCalories ? ` (${dayData.dailyCalories} kcal)` : ''}
                                                    </span>
                                                </div>
                                            ) : (
                                                <span className="text-muted">-</span>
                                            )}
                                        </td>
                                    )}
                                </tr>
                            ))}
                    </tbody>
                </Table>
            </Card.Body>
        </Card>
    );
};


const WeeklyMenuGenerator = ({ userId, userHealthGoals = [], userRestrictions = [], userPersonalInfo, onMenuGenerated }) => {
    const [viewMode, setViewMode] = useState('history');
    const [generatedMenuData, setGeneratedMenuData] = useState(null);
    const [recipeDetails, setRecipeDetails] = useState({});
    const [shoppingList, setShoppingList] = useState({});
    const [loadingGeneration, setLoadingGeneration] = useState({ menu: false, details: {}, shoppingList: false });
    const [generationError, setGenerationError] = useState('');

    const [numDays, setNumDays] = useState(7);
    const [numPeople, setNumPeople] = useState(1);
    const [numMeals, setNumMeals] = useState(3);

    const [dietPreference, setDietPreferenceState] = useState('');
    const [otherDietOrCuisine, setOtherDietOrCuisine] = useState('');
    const [avoidIngredientInput, setAvoidIngredientInput] = useState('');
    const [ingredientsToAvoidList, setIngredientsToAvoidList] = useState([]);
    const [customPromptText, setCustomPromptText] = useState('');
    const avoidInputRef = useRef(null);

    const [dayLoadingStatus, setDayLoadingStatus] = useState({});
    const [menuHistory, setMenuHistory] = useState([]);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);
    const [historyError, setHistoryError] = useState('');
    const [selectedHistoryMenu, setSelectedHistoryMenu] = useState(null);
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [historyPlanDisplayMode, setHistoryPlanDisplayMode] = useState('menu');
    const [showMealDetailModal, setShowMealDetailModal] = useState(false);
    const [selectedMealInfoForDetail, setSelectedMealInfoForDetail] = useState(null);
    const [generatorDisplayMode, setGeneratorDisplayMode] = useState('menu');

    const dietAndCuisineOptions = useMemo(() => [
        { value: '', label: 'Any / Default Healthy' }, { type: 'group', label: 'Dietary Styles' },
        { value: 'Mediterranean', label: 'Mediterranean' }, { value: 'Vegetarian', label: 'Vegetarian' },
        { value: 'Vegan', label: 'Vegan' }, { value: 'Keto', label: 'Keto / Low-Carb' },
        { value: 'Paleo', label: 'Paleo' }, { value: 'Gluten-Free', label: 'Gluten-Free' },
        { value: 'Dairy-Free', label: 'Dairy-Free' }, { value: 'Low-FODMAP', label: 'Low-FODMAP' },
        { type: 'group', label: 'Regional Cuisines' }, { value: 'Italian', label: 'Italian Inspired' },
        { value: 'Mexican', label: 'Mexican Inspired' }, { value: 'Indian', label: 'Indian Inspired' },
        { value: 'Chinese', label: 'Chinese Inspired' }, { value: 'Thai', label: 'Thai Inspired' },
        { value: 'Japanese', label: 'Japanese Inspired' }, { value: 'French', label: 'French Inspired' },
        { value: 'other', label: 'Other (Specify Diet/Cuisine)' },
    ], []);

    const effectiveOverallRestrictions = useMemo(() => {
        const profileRest = Array.isArray(userRestrictions)
            ? userRestrictions.map(r => (typeof r === 'string' ? r.trim().toLowerCase() : (r?.restriction || '').trim().toLowerCase())).filter(Boolean)
            : [];
        const localAvoids = ingredientsToAvoidList.map(r => r.trim().toLowerCase()).filter(Boolean);
        return [...new Set([...profileRest, ...localAvoids])];
    }, [userRestrictions, ingredientsToAvoidList]);

    useEffect(() => { if (numDays < 1) setNumDays(1); else if (numDays > 7) setNumDays(7); }, [numDays]);
    useEffect(() => { if (numPeople < 1) setNumPeople(1); }, [numPeople]);
    useEffect(() => { if (numMeals < 1) setNumMeals(1); else if (numMeals > 6) setNumMeals(6); }, [numMeals]);

    const fetchAndSetHistory = useCallback(async () => {
        if (userId) {
            setIsLoadingHistory(true); setHistoryError('');
            try {
                const history = await getMenuHistory(userId);
                setMenuHistory(history || []);
            } catch (err) {
                setHistoryError('Could not load menu history.'); console.error(err);
            } finally { setIsLoadingHistory(false); }
        }
    }, [userId]);

    useEffect(() => { if (viewMode === 'history') { fetchAndSetHistory(); } }, [viewMode, fetchAndSetHistory]);


    const handleAddAvoidIngredient = useCallback(() => {
        const ingredient = avoidIngredientInput.trim().toLowerCase();
        if (ingredient && !ingredientsToAvoidList.includes(ingredient)) {
            setIngredientsToAvoidList(prev => [...prev, ingredient]);
        }
        setAvoidIngredientInput('');
        if (avoidInputRef.current) avoidInputRef.current.focus();
    }, [avoidIngredientInput, ingredientsToAvoidList]);

    const handleRemoveAvoidIngredient = useCallback((ingredientToRemove) => {
        setIngredientsToAvoidList(prev => prev.filter(ing => ing !== ingredientToRemove));
    }, []);

    const generateShoppingListWithAI = useCallback(async (menuDataToProcess, forHistory = false, peopleForListArg) => {
        const currentNumPeople = peopleForListArg !== undefined ? peopleForListArg : numPeople;

        if (!forHistory) {
            setLoadingGeneration(prev => ({ ...prev, shoppingList: true }));
        }

        const allIngredients = [];
        Object.values(menuDataToProcess).forEach(day => {
            if (day && day.meals) {
                Object.values(day.meals).forEach(mealObject => {
                    if (mealObject && Array.isArray(mealObject.ingredients) && mealObject.ingredients.length > 0 && !mealObject.ingredients[0]?.startsWith("Error:")) {
                        allIngredients.push(...mealObject.ingredients);
                    }
                });
            }
        });

        const ingredientText = allIngredients.filter(Boolean).join('\n');
        if (ingredientText.trim() === '') {
            const emptyListResult = { note: "No valid ingredients to generate shopping list." };
            if (!forHistory) setShoppingList(emptyListResult);
            if (!forHistory) setLoadingGeneration(prev => ({ ...prev, shoppingList: false }));
            return emptyListResult;
        }

        const systemPrompt = `You are a grocery planning assistant. Create a consolidated shopping list from a list of ingredients, adjusting quantities for ${currentNumPeople} person(s). Input: List of ingredients (e.g., "1/2 cup chopped onion", "100g chicken breast", "salt to taste"). Instructions: 1. Multiply ingredient quantities by ${currentNumPeople}. If no quantity, assume a standard small purchasable unit (e.g., "salt (1 container)"). 2. **Convert all quantities to standard *purchasable* units (e.g., g, kg, ml, L, whole items, bunches, bags, packs). Do NOT list cooking units like 'tbsp', 'tsp', or 'cup' as final units unless it's a common selling unit.** *   **Produce:** Round fractions (1/2 cucumber) UP to the nearest whole item (1 cucumber). Convert recipe volumes (1 cup spinach) to store units (spinach (1 bag/bunch, approx 200g)). Convert "2 slices tomato" to "1 medium tomato". *   **Meat/Fish:** Use g, kg, or pieces (2 salmon fillets). *   **Dairy/Alternatives:** Use g, ml, L, or containers (yogurt (1 tub)). "1 scoop protein powder" becomes "protein powder (1 container/pouch)". *   **Pantry (grains, canned):** Use g, kg, or 'can', 'bag'. Convert recipe volumes (1/3 cup quinoa) to total needed and suggest a pack size (quinoa (1 pack, approx 250g)). *   **Spices/Condiments:** Small recipe amounts (1 tsp cumin, 2 tbsp salsa) become "cumin (1 small packet/jar)", "salsa (1 small jar)". Consolidate oils (olive oil) to "olive oil (1 small bottle)". 3.  Group similar items (e.g., "onion", "red onion" -> "Onions (total estimated whole units)"). 4.  Consolidate total quantities for identical items after conversion. 5.  Organize into categories: "Produce", "Meat & Fish", "Dairy & Alternatives", "Pantry Staples", "Spices & Condiments", "Other". 6.  Output ONLY a valid JSON object. Keys are categories, values are arrays of strings (item with quantity, e.g., "2 large onions", "Olive oil (1 small bottle)"). Example JSON (for ${currentNumPeople} people, showing conversions): { "Produce": [ "${currentNumPeople * 2} bananas", "${Math.ceil(0.5 * currentNumPeople)} cucumber${Math.ceil(0.5 * currentNumPeople) > 1 ? 's' : ''}", "Onions (approx ${Math.ceil((0.25 + 0.25) * currentNumPeople)} medium)", "Fresh spinach (1 bag, enough for ${(1.5) * currentNumPeople} cups total)", "Tomatoes (1-2 medium, for slices)" ], "Meat & Fish": ["${420 * currentNumPeople}g chicken breast"], "Pantry Staples": [ "Quinoa (1 pack, enough for ${(75 + (1 / 3 * 185)) * currentNumPeople}g uncooked)", "Canned black beans (${Math.ceil((80 / 240) * currentNumPeople)} cans)" ], "Spices & Condiments": [ "Olive oil (1 small bottle)", "Ground cumin (1 small packet/jar)" ] } Input ingredients list (quantities are per-person before multiplication by ${currentNumPeople}): ${ingredientText}`;

        try {
            const result = await callPollinationsTextApi(systemPrompt, 'Generate shopping list JSON.');
            if (result && typeof result === 'object') {
                if (!forHistory) setShoppingList(result);
                return result;
            } else {
                const errorResult = { error: "AI returned invalid shopping list format." };
                if (!forHistory) setShoppingList(errorResult);
                return errorResult;
            }
        } catch (fetchError) {
            const errorResult = { error: `Failed to generate shopping list: ${fetchError.message}` };
            if (!forHistory) {
                setGenerationError(prev => prev ? `${prev}\n${errorResult.error}` : errorResult.error);
                setShoppingList(errorResult);
            }
            return errorResult;
        } finally {
            if (!forHistory) setLoadingGeneration(prev => ({ ...prev, shoppingList: false }));
        }
    }, [numPeople, callPollinationsTextApi, setGenerationError]);


    const generateWeeklyMenu = useCallback(async () => {
        if (numDays < 1 || numDays > 7) {
            setGenerationError('Number of days must be between 1 and 7.');
            return;
        }
        if (numMeals < 1 || numMeals > 6) {
            setGenerationError('Number of meals must be between 1 and 6.');
            return;
        }
        if (dietPreference === 'other' && !otherDietOrCuisine.trim()) {
            setGenerationError('Please specify your "Other" diet or cuisine style.');
            return;
        }

        setLoadingGeneration({ menu: true, details: {}, shoppingList: false });
        setGenerationError('');
        setGeneratedMenuData(null);
        setRecipeDetails({});
        setShoppingList({});

        let healthGoalContext = '';
        if (Array.isArray(userHealthGoals) && userHealthGoals.length > 0) {
            healthGoalContext = 'User Health Goals (for overall context and to subtly guide meal selection):\n' +
                userHealthGoals.map(g => `- ${g?.target || 'General Goal'}${g?.deadline ? ` (by ${g.deadline})` : ''}`).join('\n');
        } else {
            healthGoalContext = 'User Health Goals: Not specified.';
        }

        const finalDietPrefForInitialPlan = dietPreference === 'other' ? otherDietOrCuisine.trim() : dietPreference;
        const dietPreferenceContext = finalDietPrefForInitialPlan
            ? `Dietary Preference: ${finalDietPrefForInitialPlan}. All meals must strictly adhere to this preference.`
            : 'Dietary Preference: Balanced / General Healthy.';

        const restrictionsContext = effectiveOverallRestrictions.length > 0
            ? `Strict Food Restrictions: Strictly AVOID these ingredients/foods in all meals: ${effectiveOverallRestrictions.join(', ')}. If a restricted ingredient is typical for a meal, suggest a common, suitable substitute or omit it.`
            : 'Strict Food Restrictions: No specific additional food restrictions provided beyond the general diet preference.';

        let additionalInstructionsContext = '';
        if (customPromptText.trim()) {
            additionalInstructionsContext = `Additional User Instructions (for overall plan, meal variety, and complexity):\n${customPromptText.trim()}`;
        } else {
            additionalInstructionsContext = 'Additional User Instructions: None provided.';
        }

        let dailyTargetCaloriesNum = 2000;
        let caloricTargetDescription = `Set the 'dailyCalories' for each day in the plan to approximately ${dailyTargetCaloriesNum} kcal.`;
        let macroDistributionContextString = "Aim for a generally balanced macronutrient distribution (e.g., Protein 20-30%, Carbs 40-50%, Fat 20-30% of total daily calories) unless the diet preference (e.g., Keto - high fat, low carb; High Protein - P 30-40%) strongly dictates otherwise. Calculate macros in grams.";

        if (userPersonalInfo) {
            const primaryGoal = userPersonalInfo.primary_goal?.toLowerCase().replace(/_/g, ' ') || "";

            if (userPersonalInfo.target_calories && userPersonalInfo.target_calories > 0) {
                dailyTargetCaloriesNum = userPersonalInfo.target_calories;
                caloricTargetDescription = `The user's explicit target daily calorie intake is ${dailyTargetCaloriesNum} kcal. The meal plan's 'dailyCalories' field for each day MUST reflect this value.`;
                if (primaryGoal.includes("lose weight") || primaryGoal.includes("fat loss")) caloricTargetDescription += " This target likely aims for a caloric deficit.";
                else if (primaryGoal.includes("gain muscle") || primaryGoal.includes("build muscle")) caloricTargetDescription += " This target likely aims for a caloric surplus or maintenance for muscle growth.";
            } else if (userPersonalInfo.tdee && userPersonalInfo.tdee > 0) {
                const tdee = userPersonalInfo.tdee;
                if (primaryGoal.includes("lose weight") || primaryGoal.includes("fat loss")) {
                    dailyTargetCaloriesNum = Math.round(tdee * 0.85);
                    caloricTargetDescription = `User's TDEE is ${tdee} kcal. For fat loss, set 'dailyCalories' for each day to ~${dailyTargetCaloriesNum} kcal (15% deficit).`;
                } else if (primaryGoal.includes("gain muscle") || primaryGoal.includes("build muscle")) {
                    dailyTargetCaloriesNum = Math.round(tdee * 1.1);
                    caloricTargetDescription = `User's TDEE is ${tdee} kcal. For muscle gain, set 'dailyCalories' for each day to ~${dailyTargetCaloriesNum} kcal (10% surplus).`;
                } else {
                    dailyTargetCaloriesNum = Math.round(tdee);
                    caloricTargetDescription = `User's TDEE is ${tdee} kcal. For maintenance or unspecified goal, set 'dailyCalories' for each day to ~${dailyTargetCaloriesNum} kcal.`;
                }
            } else if (userPersonalInfo.bmr && userPersonalInfo.bmr > 0) {
                const estimatedTdee = Math.round(userPersonalInfo.bmr * 1.375);
                if (primaryGoal.includes("lose weight") || primaryGoal.includes("fat loss")) {
                    dailyTargetCaloriesNum = Math.round(estimatedTdee * 0.85);
                    caloricTargetDescription = `User's BMR is ${userPersonalInfo.bmr} kcal (est. TDEE ${estimatedTdee} kcal). For fat loss, set 'dailyCalories' for each day to ~${dailyTargetCaloriesNum} kcal.`;
                } else if (primaryGoal.includes("gain muscle") || primaryGoal.includes("build muscle")) {
                    dailyTargetCaloriesNum = Math.round(estimatedTdee * 1.1);
                    caloricTargetDescription = `User's BMR is ${userPersonalInfo.bmr} kcal (est. TDEE ${estimatedTdee} kcal). For muscle gain, set 'dailyCalories' for each day to ~${dailyTargetCaloriesNum} kcal.`;
                } else {
                    dailyTargetCaloriesNum = estimatedTdee;
                    caloricTargetDescription = `User's BMR is ${userPersonalInfo.bmr} kcal (est. TDEE ${estimatedTdee} kcal). Set 'dailyCalories' for each day to ~${dailyTargetCaloriesNum} kcal.`;
                }
                caloricTargetDescription += " This is an estimation.";
            }

            if (userPersonalInfo.protein_percent_target && userPersonalInfo.carb_percent_target && userPersonalInfo.fat_percent_target) {
                const p = userPersonalInfo.protein_percent_target;
                const c = userPersonalInfo.carb_percent_target;
                const f = userPersonalInfo.fat_percent_target;
                if ((p + c + f) >= 98 && (p + c + f) <= 102) {
                    macroDistributionContextString = `The user's target macronutrient distribution is: Protein ${p}%, Carbs ${c}%, Fat ${f}%. Calculate macros in grams based on the 'dailyCalories'.`;
                } else {
                    macroDistributionContextString = `User has specified macro targets (P:${p}%, C:${c}%, F:${f}%) but they don't sum to 100%. Prioritize the diet preference (e.g., Keto) or a balanced distribution if diet is general, and then try to match these percentages as best as possible.`;
                }
            }
        }

        let mealKeys = ["meal_1"];
        if (numMeals === 1) mealKeys = ["main_meal"];
        else if (numMeals === 2) mealKeys = ["meal_1", "meal_2"];
        else if (numMeals === 3) mealKeys = ["breakfast", "lunch", "dinner"];
        else if (numMeals === 4) mealKeys = ["breakfast", "lunch", "afternoon_snack", "dinner"];
        else if (numMeals === 5) mealKeys = ["breakfast", "morning_snack", "lunch", "afternoon_snack", "dinner"];
        else if (numMeals === 6) mealKeys = ["breakfast", "morning_snack", "lunch", "afternoon_snack", "dinner", "evening_snack"];

        const mealKeyString = mealKeys.join(', ');

        const initialPlanSystemPrompt = `You are an expert nutrition AI. Your task is to generate a personalized meal plan structure based on the user's profile, preferences, and nutritional targets.

User Profile & Preferences:
- Plan Duration: ${numDays} day(s).
- Number of People: ${numPeople} (Note: the meal plan structure is for 1 person, recipe details will be scaled later if numPeople > 1. Focus on 1 person for calorie/macro targets).
- Meals Per Day: ${numMeals}.
- Meal Names to Use: ${mealKeyString}. Use these exact keys in the JSON output.
- ${dietPreferenceContext}
- ${restrictionsContext}
- ${healthGoalContext}
- ${additionalInstructionsContext}

Daily Nutritional Targets for the Plan (for 1 person):
- Target Daily Calories: ${caloricTargetDescription} The 'dailyCalories' field in your JSON output for each day MUST be exactly ${dailyTargetCaloriesNum} kcal.
- Target Macronutrient Distribution: ${macroDistributionContextString} Calculate protein, carbs, and fat in grams. (Protein & Carbs: 4 kcal/g, Fat: 9 kcal/g). The sum of macronutrient calories should closely match 'dailyCalories'.

Output Requirements:
Return ONLY a valid JSON object. Do not include any text before or after the JSON.
The JSON object should have keys "day 1", "day 2", ..., "day ${numDays}".
Each day object must contain:
1.  "dailyCalories": (Number) This MUST be ${dailyTargetCaloriesNum}.
2.  "macros": (Object) Daily total macronutrients.
    - "protein": (Number) Grams of protein.
    - "carbs": (Number) Grams of carbohydrates.
    - "fat": (Number) Grams of fat.
3.  "meals": (Object) Keys are the specified meal names (${mealKeyString}). Each meal object must contain:
    a. "mealIdea": (String) A concise, appealing name or idea for the meal (e.g., "Grilled Chicken Salad with Avocado", "Quinoa Porridge with Berries and Nuts"). This idea will be used to generate a full recipe later.
    b. "targetCaloriesPerServing": (Number) Approximate calories for THIS specific meal. The sum of 'targetCaloriesPerServing' for all meals in a day should equal 'dailyCalories' (${dailyTargetCaloriesNum}).
    c. "targetMacrosPerServing": (Object) Approximate macros for THIS meal.
        - "protein": (Number) Grams.
        - "carbs": (Number) Grams.
        - "fat": (Number) Grams.
        (The sum of macros for all meals should equal the daily total "macros").

Example for 1 day, 3 meals, ${dailyTargetCaloriesNum} kcal target (actual macros will depend on distribution guidance):
{
  "day 1": {
    "dailyCalories": ${dailyTargetCaloriesNum},
    "macros": { "protein": ${Math.round(dailyTargetCaloriesNum * 0.25 / 4)}, "carbs": ${Math.round(dailyTargetCaloriesNum * 0.45 / 4)}, "fat": ${Math.round(dailyTargetCaloriesNum * 0.30 / 9)} },
    "meals": {
      "breakfast": { "mealIdea": "Scrambled Eggs with Spinach and Whole Wheat Toast", "targetCaloriesPerServing": ${Math.round(dailyTargetCaloriesNum * 0.3)}, "targetMacrosPerServing": { "protein": ${Math.round(dailyTargetCaloriesNum * 0.3 * 0.3 / 4)}, "carbs": ${Math.round(dailyTargetCaloriesNum * 0.3 * 0.4 / 4)}, "fat": ${Math.round(dailyTargetCaloriesNum * 0.3 * 0.3 / 9)} } },
      "lunch": { "mealIdea": "Lentil Soup with a Side Salad", "targetCaloriesPerServing": ${Math.round(dailyTargetCaloriesNum * 0.4)}, "targetMacrosPerServing": { "protein": ${Math.round(dailyTargetCaloriesNum * 0.4 * 0.25 / 4)}, "carbs": ${Math.round(dailyTargetCaloriesNum * 0.4 * 0.5 / 4)}, "fat": ${Math.round(dailyTargetCaloriesNum * 0.4 * 0.25 / 9)} } },
      "dinner": { "mealIdea": "Baked Salmon with Roasted Asparagus and Quinoa", "targetCaloriesPerServing": ${Math.round(dailyTargetCaloriesNum * 0.3)}, "targetMacrosPerServing": { "protein": ${Math.round(dailyTargetCaloriesNum * 0.3 * 0.35 / 4)}, "carbs": ${Math.round(dailyTargetCaloriesNum * 0.3 * 0.35 / 4)}, "fat": ${Math.round(dailyTargetCaloriesNum * 0.3 * 0.3 / 9)} } }
    }
  }
}
Adhere strictly to dietary preferences and restrictions. Ensure variety across days and meals.
Prioritize nutrient-dense foods. Meal ideas should be relatively common and use accessible ingredients.
Return ONLY the valid JSON.
`;
        try {
            const initialPlanStructure = await callPollinationsTextApi(
                initialPlanSystemPrompt,
                `Generate ${numDays}-day meal plan structure with ${numMeals} meals/day, ${dailyTargetCaloriesNum} kcal/day.`
            );

            if (!initialPlanStructure || typeof initialPlanStructure !== 'object' || Object.keys(initialPlanStructure).length === 0) {
                throw new Error('Invalid or empty initial plan structure received from AI. Expected a JSON object.');
            }

            const populatedMenuData = {};
            setLoadingGeneration(prev => ({ ...prev, menu: false, details: {} }));

            const initialMenuDisplay = {};
            const initialDayStatus = {};

            for (let i = 1; i <= numDays; i++) {
                const dayKey = `day ${i}`;
                const dayDataFromInitialPlan = initialPlanStructure[dayKey];

                if (!dayDataFromInitialPlan || typeof dayDataFromInitialPlan.meals !== 'object' ||
                    typeof dayDataFromInitialPlan.dailyCalories !== 'number' || dayDataFromInitialPlan.dailyCalories !== dailyTargetCaloriesNum ||
                    typeof dayDataFromInitialPlan.macros !== 'object') {

                    console.warn(`Skipping ${dayKey} (initial setup) due to invalid structure or calorie mismatch. Expected ${dailyTargetCaloriesNum}, got ${dayDataFromInitialPlan?.dailyCalories}.`);
                    initialMenuDisplay[dayKey] = {
                        dailyCalories: dailyTargetCaloriesNum,
                        macros: { protein: 0, carbs: 0, fat: 0 },
                        meals: {},
                        error: `Failed to process ${dayKey} structure from AI or calorie mismatch.`
                    };
                    mealKeys.forEach(mKey => {
                        initialMenuDisplay[dayKey].meals[mKey] = {
                            recipeName: "Error: Day plan structure invalid",
                            estimatedCaloriesPerServing: 0, macrosPerServing: { protein: 0, carbs: 0, fat: 0 },
                            ingredients: [], steps: [], notes: []
                        };
                    });
                    initialDayStatus[dayKey] = "error_structure";
                    populatedMenuData[dayKey] = JSON.parse(JSON.stringify(initialMenuDisplay[dayKey]));
                } else {
                    initialMenuDisplay[dayKey] = {
                        dailyCalories: dayDataFromInitialPlan.dailyCalories,
                        macros: dayDataFromInitialPlan.macros,
                        meals: {}
                    };
                    populatedMenuData[dayKey] = {
                        dailyCalories: dayDataFromInitialPlan.dailyCalories,
                        macros: dayDataFromInitialPlan.macros,
                        meals: {}
                    };

                    mealKeys.forEach(mealKey => {
                        const mealTargetInfo = dayDataFromInitialPlan.meals[mealKey];
                        if (mealTargetInfo && mealTargetInfo.mealIdea) {
                            initialMenuDisplay[dayKey].meals[mealKey] = {
                                recipeName: `${mealTargetInfo.mealIdea} (Loading details...)`,
                                estimatedCaloriesPerServing: mealTargetInfo.targetCaloriesPerServing || 0,
                                macrosPerServing: mealTargetInfo.targetMacrosPerServing || { protein: 0, carbs: 0, fat: 0 },
                            };
                        } else {
                            initialMenuDisplay[dayKey].meals[mealKey] = { recipeName: "Error: Missing meal idea" };
                            populatedMenuData[dayKey].meals[mealKey] = { recipeName: "Error: Missing meal idea from initial plan" };
                        }
                    });
                    initialDayStatus[dayKey] = "pending";
                }
            }
            setGeneratedMenuData(initialMenuDisplay);
            setDayLoadingStatus(initialDayStatus);

            for (let i = 1; i <= numDays; i++) {
                const dayKey = `day ${i}`;

                if (initialDayStatus[dayKey] === "error_structure") {
                    console.log(`Skipping detail generation for ${dayKey} due to initial structure error.`);
                    continue;
                }

                setDayLoadingStatus(prev => ({ ...prev, [dayKey]: "loading" }));
                console.log(`Starting to process meals for ${dayKey}...`);

                const dayDataFromInitialPlan = initialPlanStructure[dayKey];
                const mealDetailPromisesForDay = [];

                for (const mealKey of mealKeys) {
                    const mealTargets = dayDataFromInitialPlan.meals[mealKey];
                    const loadingDetailKey = `${dayKey}-${mealKey}`;

                    if (!mealTargets || typeof mealTargets.mealIdea !== 'string' || mealTargets.mealIdea.trim() === "" ||
                        typeof mealTargets.targetCaloriesPerServing !== 'number' || typeof mealTargets.targetMacrosPerServing !== 'object') {

                        console.warn(`Skipping meal ${mealKey} on ${dayKey} (details) due to invalid target structure.`);
                        populatedMenuData[dayKey].meals[mealKey] = {
                            recipeName: `Error: Invalid data for ${mealKey}`,
                            estimatedCaloriesPerServing: mealTargets?.targetCaloriesPerServing || 0,
                            macrosPerServing: mealTargets?.targetMacrosPerServing || { protein: 0, carbs: 0, fat: 0 },
                            ingredients: [], steps: [], notes: ["Meal target data from initial plan was invalid."]
                        };
                        setGeneratedMenuData(prevData => {
                            const newData = JSON.parse(JSON.stringify(prevData));
                            if (newData[dayKey] && newData[dayKey].meals) {
                                newData[dayKey].meals[mealKey] = populatedMenuData[dayKey].meals[mealKey];
                            }
                            return newData;
                        });
                        continue;
                    }

                    setLoadingGeneration(prev => ({ ...prev, details: { ...prev.details, [loadingDetailKey]: true } }));

                    const promise = generateSingleRecipeDetailsAI({
                        mealIdea: mealTargets.mealIdea,
                        targetCalories: mealTargets.targetCaloriesPerServing,
                        targetMacros: mealTargets.targetMacrosPerServing,
                        servings: numPeople,
                        dietPreference: dietPreference,
                        otherDietOrCuisine: otherDietOrCuisine,
                        customPromptText: "",
                        effectiveOverallRestrictions: effectiveOverallRestrictions,
                        userHealthGoals: userHealthGoals,
                    })
                        .then(detailedRecipeObject => {
                            if (detailedRecipeObject && typeof detailedRecipeObject === 'object') {
                                populatedMenuData[dayKey].meals[mealKey] = detailedRecipeObject;
                            } else {
                                throw new Error('Received invalid detailed recipe object.');
                            }
                        })
                        .catch(err => {
                            console.error(`Error generating recipe details for ${loadingDetailKey} ("${mealTargets.mealIdea}"):`, err);
                            populatedMenuData[dayKey].meals[mealKey] = {
                                recipeName: `${mealTargets.mealIdea} (Error)`,
                                estimatedCaloriesPerServing: mealTargets.targetCaloriesPerServing,
                                macrosPerServing: mealTargets.targetMacrosPerServing,
                                ingredients: ["Error: Could not generate ingredients."],
                                steps: ["Error: Could not generate steps."],
                                notes: [`Error generating recipe: ${err.message}`]
                            };
                        })
                        .finally(() => {
                            setLoadingGeneration(prev => ({ ...prev, details: { ...prev.details, [loadingDetailKey]: false } }));
                            setGeneratedMenuData(prevData => {
                                const newData = JSON.parse(JSON.stringify(prevData));
                                if (newData[dayKey] && newData[dayKey].meals) {
                                    newData[dayKey].meals[mealKey] = populatedMenuData[dayKey].meals[mealKey];
                                }
                                return newData;
                            });
                        });
                    mealDetailPromisesForDay.push(promise);
                }

                await Promise.allSettled(mealDetailPromisesForDay);
                console.log(`Finished processing meals for ${dayKey}.`);
                setDayLoadingStatus(prev => ({ ...prev, [dayKey]: "completed" }));

            }

            console.log("All recipe details processed. Generating shopping list...");
            let shoppingListForSaving;

            let hasValidMealsForShoppingList = Object.values(populatedMenuData).some(day =>
                day.meals && Object.values(day.meals).some(meal =>
                    meal.ingredients && meal.ingredients.length > 0 && !meal.ingredients[0]?.startsWith("Error:")
                )
            );

            if (hasValidMealsForShoppingList) {
                shoppingListForSaving = await generateShoppingListWithAI(populatedMenuData, false, numPeople);
            } else {
                console.warn("No valid meals for shopping list generation.");
                shoppingListForSaving = { note: "Could not generate shopping list due to missing meal details or errors." };
                setShoppingList(shoppingListForSaving);
            }
            console.log("Shopping list generation attempt complete. List to save:", shoppingListForSaving);


            if (onMenuGenerated && typeof onMenuGenerated === 'function') {
                console.log("Saving menu and shopping list to history...");

                console.log("Populated menu data for saving:", JSON.stringify(populatedMenuData, null, 2));
                await onMenuGenerated(
                    {
                        menuData: populatedMenuData,
                        metadata: {
                            numPeople: numPeople,
                            dietPreference: dietPreference,
                            otherDietOrCuisine: otherDietOrCuisine,
                            numDays: numDays,
                            numMeals: numMeals
                        },
                        shoppingList: shoppingListForSaving
                    },
                    true
                );
                if (fetchAndSetHistory) fetchAndSetHistory();
                console.log("Menu and shopping list saved.");
            } else {
                console.log("onMenuGenerated not available. Skipping save.");
            }

        } catch (mainError) {
            console.error("Error in weekly menu generation pipeline:", mainError);
            setGenerationError(`Error: ${mainError.message}.`);
        } finally {
            setLoadingGeneration({ menu: false, details: {}, shoppingList: false });
        }
    }, [
        numDays, numPeople, numMeals, dietPreference, otherDietOrCuisine, customPromptText,
        userHealthGoals, userPersonalInfo, effectiveOverallRestrictions,
        callPollinationsTextApi,
        generateShoppingListWithAI,
        onMenuGenerated, fetchAndSetHistory,
        setGenerationError, setGeneratedMenuData, setLoadingGeneration,
        setRecipeDetails, setShoppingList, setDayLoadingStatus
    ]);


    const handleShowGeneratorView = () => {
        setGeneratedMenuData(null); setRecipeDetails({}); setShoppingList({}); setGenerationError('');
        setNumDays(7); setNumPeople(1); setNumMeals(3);
        setDietPreferenceState(''); setOtherDietOrCuisine('');
        setIngredientsToAvoidList([]); setCustomPromptText('');
        setGeneratorDisplayMode('menu');
        setViewMode('generator');
    };
    const handleShowHistoryView = () => { setViewMode('history'); };

    const handleShowHistoryDetails = (menuItem) => {
        // Log the structure of the item being opened
        //console.log("Selected history item for modal:", JSON.stringify(menuItem, null, 2));
        setSelectedHistoryMenu(menuItem);
        setHistoryPlanDisplayMode('menu');
        setShowHistoryModal(true);
    };
    const handleCloseHistoryModal = () => {
        setSelectedHistoryMenu(null);
        setShowHistoryModal(false);
        setHistoryPlanDisplayMode('menu');
    };

    const handleHideMenuFromHistory = async (menuIdToHide, e) => {
        e.stopPropagation(); if (!menuIdToHide || !userId) { setHistoryError("Cannot hide menu."); return; }
        if (window.confirm("Hide this menu?")) {
            setIsLoadingHistory(true);
            try {
                const success = await hideUserActivity(menuIdToHide, userId);
                if (success) { setMenuHistory(prev => prev.filter(item => item.id !== menuIdToHide)); setHistoryError(''); }
                else { setHistoryError("Failed to hide menu."); }
            } catch (err) { setHistoryError("Error hiding menu."); console.error(err); }
            finally { setIsLoadingHistory(false); }
        }
    };

    const openMealDetailModal = useCallback((dayLabel, mealType, mealRecipeData) => {
        const key = `${dayLabel}-${mealType.replace(/\s+/g, '_')}`;
        setSelectedMealInfoForDetail({
            key,
            dayLabel,
            mealType,
            recipeData: mealRecipeData
        });
        setShowMealDetailModal(true);
    }, [setSelectedMealInfoForDetail, setShowMealDetailModal]);

    const closeMealDetailModal = useCallback(() => { setShowMealDetailModal(false); setSelectedMealInfoForDetail(null); }, []);

    const handleCalendarEventClick = useCallback((clickInfo) => {
        const { dayLabel, mealType, mealObject } = clickInfo.event.extendedProps;
        if (dayLabel && mealType && mealObject && mealObject.recipeName) {
            openMealDetailModal(dayLabel, mealType, mealObject);
        } else {
            console.error('Calendar event missing data or full mealObject:', clickInfo.event);
        }
    }, [openMealDetailModal]);

    const generateCalendarEventsForDisplay = useCallback((menuToDisplay) => {
        if (!menuToDisplay || typeof menuToDisplay !== 'object') return [];
        const today = new Date();
        const events = [];
        const mealTimes = {
            breakfast: '08:00:00', morning_snack: '10:30:00', lunch: '13:00:00',
            afternoon_snack: '16:00:00', dinner: '19:00:00', evening_snack: '21:00:00',
            "main_meal": '14:00:00', "meal_1": '10:00:00', "meal_2": '17:00:00'
        };
        const mealColors = {
            breakfast: '#17a2b8', morning_snack: '#6f42c1', lunch: '#28a745',
            afternoon_snack: '#fd7e14', dinner: '#dc3545', evening_snack: '#20c997',
            "main_meal": '#0d6efd', "meal_1": '#17a2b8', "meal_2": '#28a745'
        };

        Object.entries(menuToDisplay).forEach(([dayLabel, dayDetails], index) => {
            if (!dayDetails || !dayDetails.meals || typeof dayDetails.meals !== 'object') return;

            let dayNumber = 0;
            const match = dayLabel.match(/day\s+(\d+)/i);
            dayNumber = match ? parseInt(match[1]) - 1 : index;
            const date = new Date(today);
            date.setDate(today.getDate() + dayNumber);
            const dateStr = date.toISOString().split('T')[0];

            Object.entries(dayDetails.meals).forEach(([mealName, mealObject]) => {
                if (mealObject && typeof mealObject.recipeName === 'string' && typeof mealObject.estimatedCaloriesPerServing === 'number') {
                    const mealKey = mealName.toLowerCase().replace(/\s+/g, '_');
                    const capitalizedMealName = mealName.replace(/_/g, ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

                    events.push({
                        id: `${dayLabel}-${mealKey}`,
                        title: `${capitalizedMealName}: ${mealObject.recipeName} (${mealObject.estimatedCaloriesPerServing} kcal)`,
                        start: `${dateStr}T${mealTimes[mealKey] || mealTimes[mealName] || '12:00:00'}`,
                        backgroundColor: mealColors[mealKey] || mealColors[mealName] || '#6c757d',
                        borderColor: mealColors[mealKey] || mealColors[mealName] || '#6c757d',
                        extendedProps: {
                            dayLabel,
                            mealType: mealName,
                            mealObject: mealObject
                        }
                    });
                }
            });
        });
        return events;
    }, []);


    const renderGeneratorForm = () => (
        <Form className="mb-4" onSubmit={(e) => { e.preventDefault(); generateWeeklyMenu(); }}>
            <Card className="mb-4 p-3 bg-light border shadow-sm">
                <h5 className="text-secondary mb-3"><FontAwesomeIcon icon={faSeedling} className="me-2" />Plan General</h5>
                <Row className="g-3">
                    <Col md={4}><Form.Group controlId="formNumDays"><Form.Label>Number of Days (1-7)</Form.Label><Form.Control type="number" value={numDays} min={1} max={7} onChange={e => setNumDays(parseInt(e.target.value) || 1)} required /></Form.Group></Col>
                    <Col md={4}><Form.Group controlId="formNumPeople"><Form.Label><FontAwesomeIcon icon={faUsers} className="me-1" /> For People</Form.Label><Form.Control type="number" value={numPeople} min={1} onChange={e => setNumPeople(parseInt(e.target.value) || 1)} required /></Form.Group></Col>
                    <Col md={4}><Form.Group controlId="formNumMeals"><Form.Label><FontAwesomeIcon icon={faUtensils} className="me-1" /> Meals per Day (1-6)</Form.Label><Form.Control type="number" value={numMeals} min={1} max={6} onChange={e => setNumMeals(parseInt(e.target.value) || 3)} required /></Form.Group></Col>
                </Row>
            </Card>

            <PreferenceForm
                dietPreference={dietPreference}
                onDietPreferenceChange={setDietPreferenceState}
                otherDietOrCuisine={otherDietOrCuisine}
                onOtherDietOrCuisineChange={setOtherDietOrCuisine}
                avoidIngredientInput={avoidIngredientInput}
                onAvoidIngredientInputChange={setAvoidIngredientInput}
                ingredientsToAvoidList={ingredientsToAvoidList}
                onAddAvoidIngredient={handleAddAvoidIngredient}
                onRemoveAvoidIngredient={handleRemoveAvoidIngredient}
                customPromptText={customPromptText}
                onCustomPromptTextChange={setCustomPromptText}
                dietAndCuisineOptions={dietAndCuisineOptions}
                userRestrictions={userRestrictions}
                isLoading={loadingGeneration.menu || Object.values(loadingGeneration.details).some(v => v)}
                avoidInputRef={avoidInputRef}
                customPromptPlaceholder="e.g., prefer quick dinners, include a fish day for the overall plan"
            />
            {(effectiveOverallRestrictions.length > 0 || (userHealthGoals && userHealthGoals.length > 0)) && (
                <Row className="g-2 mb-3">
                    {effectiveOverallRestrictions.length > 0 && (
                        <Col xs={12} md={(userHealthGoals && userHealthGoals.length > 0) ? 6 : 12}>
                            <Alert variant="light" className="p-2 small border text-info mb-0 h-100 shadow-sm">
                                <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                                Using combined restrictions: <strong>{effectiveOverallRestrictions.join(', ')}</strong>
                            </Alert>
                        </Col>
                    )}
                    {(userHealthGoals && userHealthGoals.length > 0) && (
                        <Col xs={12} md={(effectiveOverallRestrictions.length > 0) ? 6 : 12}>
                            <Alert variant="light" className="p-2 small border text-info mb-0 h-100 shadow-sm">
                                <FontAwesomeIcon icon={faBullseye} className="me-2" />
                                Using profile goals: <strong>{userHealthGoals.map(g => g.target).join(', ')}</strong>
                            </Alert>
                        </Col>
                    )}
                </Row>
            )}

            <div className="text-center mt-4">
                <Button type="submit" variant="primary" size="lg" className="shadow-sm"
                    disabled={loadingGeneration.menu || Object.values(loadingGeneration.details).some(v => v) || loadingGeneration.shoppingList}
                    style={{ minWidth: '250px' }}
                >
                    {loadingGeneration.menu ? <><Spinner size="sm" animation="border" className="me-2" /> Generating Plan...</> : <><FontAwesomeIcon icon={faCalendarAlt} className="me-2" />Generate Weekly Menu</>}
                </Button>
            </div>
            {(Object.values(loadingGeneration.details).some(v => v) || loadingGeneration.shoppingList) && !loadingGeneration.menu &&
                <div className="text-center mt-2"><Spinner size="sm" animation="grow" variant="secondary" title="Fetching details/list..." /> <span className="small text-muted">Generating recipes & shopping list...</span></div>
            }
        </Form>
    );

    const renderMenuDisplayViews = (menuToDisplay) => {
        if (!menuToDisplay || Object.keys(menuToDisplay).length === 0) {
            return !loadingGeneration.menu ? <Alert variant="light" className="text-center">Generate a menu to see it here.</Alert> : null;
        }
        const currentDisplayMenu = menuToDisplay;

        const renderCalendarViewLocal = () => (
            <Card className="shadow-sm mb-4">
                <Card.Header className="bg-primary text-white"><FontAwesomeIcon icon={faCalendarAlt} className="me-2" />Meal Calendar</Card.Header>
                <Card.Body style={{ minHeight: '500px' }}>
                    <FullCalendar
                        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                        initialView="timeGridWeek"
                        events={generateCalendarEventsForDisplay(currentDisplayMenu)}
                        height="auto"
                        headerToolbar={{ left: 'prev,next today', center: 'title', right: 'dayGridMonth,timeGridWeek' }}
                        eventClick={handleCalendarEventClick}
                        eventTimeFormat={{ hour: 'numeric', minute: '2-digit', meridiem: 'short' }}
                        slotMinTime="07:00:00" slotMaxTime="22:00:00" allDaySlot={false}
                    />
                </Card.Body>
            </Card>
        );

        const renderShoppingListViewLocal = () => (
            <Card className="shadow-sm mb-4">
                <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
                    <span> <FontAwesomeIcon icon={faListAlt} className="me-2" />Shopping List ({numPeople} {numPeople > 1 ? 'people' : 'person'}) </span>
                    {loadingGeneration.shoppingList && <Spinner animation="border" size="sm" variant="light" />}
                </Card.Header>
                <Card.Body>
                    {loadingGeneration.shoppingList && <div className="text-center"><Spinner /> <p>Generating list...</p></div>}
                    {!loadingGeneration.shoppingList && Object.keys(shoppingList).length > 0 ? (
                        Object.keys(shoppingList).length === 1 && (shoppingList.error || shoppingList.note) ? (
                            <Alert variant={shoppingList.error ? "danger" : "info"}>{shoppingList.error || shoppingList.note}</Alert>
                        ) : (
                            Object.entries(shoppingList).map(([category, items]) => (
                                <div key={category} className="mb-3">
                                    <h5 className="text-primary">{category}</h5>
                                    {Array.isArray(items) && items.length > 0 ? (
                                        <ul className="list-group list-group-flush">{items.map((itemData, idx) => (
                                            <li key={idx} className="list-group-item d-flex justify-content-between align-items-center border-0 px-0 py-1">
                                                <span><FontAwesomeIcon icon={faCheckCircle} className="text-success me-2" />{String(itemData)}</span></li>
                                        ))}</ul>
                                    ) : <p className="text-muted fst-italic ps-1">No items listed for this category.</p>}
                                </div>
                            ))
                        )
                    ) : (!loadingGeneration.shoppingList && <p className="text-muted">Shopping list is empty or was not generated for this menu.</p>)}
                </Card.Body>
            </Card>
        );
        return (
            <>
                <Row className="mb-3 justify-content-center">
                    <Col xs="auto">
                        <ToggleButtonGroup type="radio" name="generatorDisplayOptions" value={generatorDisplayMode} onChange={(val) => setGeneratorDisplayMode(val)}>
                            <ToggleButton id="gen-view-menu" variant={generatorDisplayMode === 'menu' ? 'info' : 'outline-info'} value="menu"><FontAwesomeIcon icon={faTableCellsLarge} className="me-2" />Menu Table</ToggleButton>
                            <ToggleButton id="gen-view-calendar" variant={generatorDisplayMode === 'calendar' ? 'info' : 'outline-info'} value="calendar"><FontAwesomeIcon icon={faCalendarAlt} className="me-2" />Calendar</ToggleButton>
                            <ToggleButton id="gen-view-shopping" variant={generatorDisplayMode === 'shopping' ? 'info' : 'outline-info'} value="shopping"><FontAwesomeIcon icon={faListAlt} className="me-2" />Shopping List</ToggleButton>
                        </ToggleButtonGroup>
                    </Col>
                </Row>
                {generatorDisplayMode === 'menu' && <StandaloneMenuTable menuData={currentDisplayMenu} onOpenMealDetail={openMealDetailModal} isHistoryContext={false} />}
                {generatorDisplayMode === 'calendar' && renderCalendarViewLocal()}
                {generatorDisplayMode === 'shopping' && renderShoppingListViewLocal()}
            </>
        );
    };

    const renderHistoryPlanDetailsModalContent = (historyItem) => {
        if (!historyItem) return <Alert variant="light">No history item selected.</Alert>;

        //console.log("Rendering history plan details for item:", JSON.stringify(historyItem, null, 2));
        const menuDetails = historyItem?.menu;
        const menuData = menuDetails?.menuData;
        const shoppingListData = menuDetails?.shoppingList;
        const numPeopleForList = menuDetails?.metadata?.numPeople;

        const renderCurrentHistoryView = () => {
            switch (historyPlanDisplayMode) {
                case 'calendar':
                    if (!menuData) return <Alert variant="warning">Menu data for calendar is not available. Check console for details on the selected history item.</Alert>;
                    return (
                        <Card className="shadow-sm mb-4">
                            <Card.Header className="bg-info text-white"><FontAwesomeIcon icon={faCalendarAlt} className="me-2" />Meal Calendar</Card.Header>
                            <Card.Body style={{ minHeight: '500px' }}>
                                <FullCalendar
                                    plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                                    initialView="timeGridWeek"
                                    events={generateCalendarEventsForDisplay(menuData)}
                                    height="auto"
                                    headerToolbar={{ left: 'prev,next today', center: 'title', right: 'dayGridMonth,timeGridWeek' }}
                                    eventClick={handleCalendarEventClick}
                                    eventTimeFormat={{ hour: 'numeric', minute: '2-digit', meridiem: 'short' }}
                                    slotMinTime="07:00:00" slotMaxTime="22:00:00" allDaySlot={false}
                                />
                            </Card.Body>
                        </Card>
                    );
                case 'shoppingList':
                    if (!shoppingListData) {
                        return <Alert variant="warning">Shopping list data is not available for this historical plan. Check console for details.</Alert>;
                    }
                    if (shoppingListData.error && typeof shoppingListData.error === 'string') {
                        return <Alert variant="danger">{shoppingListData.error}</Alert>;
                    }
                    if (shoppingListData.note && typeof shoppingListData.note === 'string' && Object.keys(shoppingListData).length === 1) {
                        return <Alert variant="info">{shoppingListData.note}</Alert>;
                    }
                    const categories = Object.keys(shoppingListData).filter(k => k !== 'error' && k !== 'note');
                    if (categories.length === 0 && !(shoppingListData.error || shoppingListData.note)) {
                        return <Alert variant="info">The shopping list is empty or contains no valid categories.</Alert>;
                    }

                    return (
                        <Card className="shadow-sm mb-4">
                            <Card.Header className="bg-success text-white">
                                <FontAwesomeIcon icon={faListAlt} className="me-2" />Shopping List {numPeopleForList ? `(for ${numPeopleForList} ${numPeopleForList > 1 ? 'people' : 'person'})` : ''}
                            </Card.Header>
                            <Card.Body>
                                {categories.map(category => {
                                    const items = shoppingListData[category];
                                    if (!Array.isArray(items)) {
                                        console.warn(`Shopping list category "${category}" does not contain an array of items.`, items);
                                        return (
                                            <div key={category} className="mb-3">
                                                <h5 className="text-primary">{category}</h5>
                                                <p className="text-danger fst-italic ps-1">Invalid item data for this category.</p>
                                            </div>
                                        );
                                    }
                                    return (
                                        <div key={category} className="mb-3">
                                            <h5 className="text-primary">{category}</h5>
                                            {items.length > 0 ? (
                                                <ul className="list-group list-group-flush">{items.map((itemData, idx) => (
                                                    <li key={idx} className="list-group-item d-flex justify-content-between align-items-center border-0 px-0 py-1">
                                                        <span><FontAwesomeIcon icon={faCheckCircle} className="text-success me-2" />{String(itemData)}</span></li>
                                                ))}</ul>
                                            ) : <p className="text-muted fst-italic ps-1">No items listed for this category.</p>}
                                        </div>
                                    );
                                })}
                            </Card.Body>
                        </Card>
                    );
                case 'menu':
                default:
                    if (!menuData) return <Alert variant="warning">Menu data is not available. Check console for details on the selected history item.</Alert>;
                    return <StandaloneMenuTable menuData={menuData} onOpenMealDetail={openMealDetailModal} isHistoryContext={true} />;
            }
        };

        return (
            <>
                <Row className="mb-3 justify-content-center">
                    <Col xs="auto">
                        <ToggleButtonGroup
                            type="radio"
                            name="historyPlanDisplayOptions"
                            value={historyPlanDisplayMode}
                            onChange={(val) => setHistoryPlanDisplayMode(val)}
                        >
                            <ToggleButton id="hist-view-menu" variant={historyPlanDisplayMode === 'menu' ? 'info' : 'outline-info'} value="menu"><FontAwesomeIcon icon={faTableCellsLarge} className="me-2" />Menu</ToggleButton>
                            <ToggleButton id="hist-view-calendar" variant={historyPlanDisplayMode === 'calendar' ? 'info' : 'outline-info'} value="calendar"><FontAwesomeIcon icon={faCalendarAlt} className="me-2" />Calendar</ToggleButton>
                            <ToggleButton id="hist-view-shopping" variant={historyPlanDisplayMode === 'shoppingList' ? 'info' : 'outline-info'} value="shoppingList"><FontAwesomeIcon icon={faListAlt} className="me-2" />Shopping List</ToggleButton>
                        </ToggleButtonGroup>
                    </Col>
                </Row>
                {renderCurrentHistoryView()}
            </>
        );
    };

    const renderMealDetailContentUsingRecipeDisplay = () => {
        if (!selectedMealInfoForDetail || !selectedMealInfoForDetail.recipeData) return <p>No meal data selected.</p>;
        const recipeDataForDisplay = selectedMealInfoForDetail.recipeData;
        const ingredientStepDetails = recipeDetails[selectedMealInfoForDetail.key];
        const combinedRecipeData = {
            ...recipeDataForDisplay,
            ingredients: recipeDataForDisplay.ingredients || ingredientStepDetails?.ingredients || [],
            steps: recipeDataForDisplay.steps || ingredientStepDetails?.steps || [],
        };
        const isLoadingExtraDetails = loadingGeneration.details && loadingGeneration.details[selectedMealInfoForDetail.key];

        return (
            <>
                {isLoadingExtraDetails && <div className='text-center'><Spinner animation="border" variant="secondary" /><p>Loading ingredients & steps...</p></div>}
                {!isLoadingExtraDetails && <RecipeDisplay recipeData={combinedRecipeData} isNewlyGenerated={false} />}
            </>
        );
    };

    const renderMenuHistoryItemSummary = (historyItem) => {
        const menuDetails = historyItem?.menu;
        const menuData = menuDetails?.menuData;
        const metadata = menuDetails?.metadata;


        const shoppingListExists = !!menuDetails?.shoppingList &&
            Object.keys(menuDetails.shoppingList).length > 0 &&
            !menuDetails.shoppingList.error &&
            !menuDetails.shoppingList.note;


        return (
            <>
                <div className="fw-bold"><FontAwesomeIcon icon={faCalendarAlt} className="me-2 text-primary" />{historyItem.name || "Weekly Menu Plan"}</div>
                <span className="text-muted small">{historyItem.summary || `Plan for ${Object.keys(menuData || {}).length} days`}</span>
                {metadata?.numPeople && <Badge pill bg="light" text="dark" className="ms-2 small">For {metadata.numPeople} {metadata.numPeople > 1 ? "people" : "person"}</Badge>}
                {shoppingListExists && <Badge pill bg="light" text="dark" className="ms-2 small"><FontAwesomeIcon icon={faListAlt} /> Shopping List</Badge>}
            </>
        );
    }


    return (
        <>
            <ViewModeLayout
                viewMode={viewMode}
                onShowGeneratorView={handleShowGeneratorView}
                onShowHistoryView={handleShowHistoryView}
                historyTitle="My Weekly Menu Plans"
                generatorTitle="Weekly Menu Planner"
                historyIcon={faHistory}
                generatorIcon={faCalendarAlt}
                newItemButtonText="New Menu Plan"
                headerStyle={{ background: 'linear-gradient(to right, var(--bs-info), var(--bs-cyan))' }}
            >
                {viewMode === 'history' ? (

                    <HistoryView
                        items={menuHistory}
                        isLoading={isLoadingHistory}
                        error={historyError}
                        onSelectItem={handleShowHistoryDetails}
                        onHideItem={handleHideMenuFromHistory}
                        renderItemSummary={renderMenuHistoryItemSummary}
                        searchPlaceholder="Search menu plans..."
                        itemTypePlural="Menu Plans"
                    />
                ) : (
                    <>
                        {renderGeneratorForm()}
                        {generationError && <Alert variant="danger" className="mt-4">{generationError}</Alert>}
                        {(loadingGeneration.menu || Object.values(loadingGeneration.details).some(v => v)) &&
                            <div className="text-center my-5">
                                <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
                                <p className="mt-2">
                                    {loadingGeneration.menu ? "Generating plan structure..." : "Generating recipe details..."}
                                </p>
                            </div>
                        }
                        {generatedMenuData && !loadingGeneration.menu && renderMenuDisplayViews(generatedMenuData)}
                    </>
                )}
            </ViewModeLayout>

            <ItemDetailsModal
                show={showHistoryModal}
                onHide={handleCloseHistoryModal}
                itemData={selectedHistoryMenu}
                renderItemDetailsContent={renderHistoryPlanDetailsModalContent}
                modalTitle={selectedHistoryMenu?.name || "Menu Plan Details"}
                size="xl"
            />

            <BootstrapModal show={showMealDetailModal} onHide={closeMealDetailModal} centered size="xl">
                <BootstrapModal.Header closeButton>
                    <BootstrapModal.Title className="text-capitalize">
                        {selectedMealInfoForDetail?.recipeData?.recipeName || selectedMealInfoForDetail?.mealType?.replace(/_/g, ' ')}
                        {selectedMealInfoForDetail?.dayLabel && ` - ${selectedMealInfoForDetail.dayLabel.replace('day ', 'Day ')}`}
                    </BootstrapModal.Title>
                </BootstrapModal.Header>
                <BootstrapModal.Body>
                    {selectedMealInfoForDetail && renderMealDetailContentUsingRecipeDisplay()}
                </BootstrapModal.Body>
                <BootstrapModal.Footer>
                    <Button variant="outline-secondary" onClick={closeMealDetailModal}>Close</Button>
                </BootstrapModal.Footer>
            </BootstrapModal>

            <style jsx global>{`
                .bg-primary-soft { background-color: rgba(var(--bs-primary-rgb), 0.1); }
                .section-title { margin-top: 1.5rem; margin-bottom: 0.5rem; }
                .divider { height: 1px; background-color: #e0e0e0; margin-bottom: 1rem; }
                .badge-remove-btn:hover { color: var(--bs-danger) !important; }
            `}</style>
        </>
    );
};

export default WeeklyMenuGenerator;